import { gql, useQuery } from '@apollo/client';
import React from 'react';
import {
  EmbedFooterLink,
  EmbedFooterLinkTitle,
  EmbedFooterLogoContainer,
} from './EmbedFooter';
import SVGIcon from '../../components/SVGIcon';
import slabIcon from '../../icons/prefilled/slab.svg';
import postPath from '../../utils/url/postPath';
import fullOrgUrl from '../../utils/url/fullOrgUrl';
import type {
  PostEmbedFooterPublicQuery,
  PostEmbedFooterPublicQueryVariables,
  PostEmbedFooterQuery,
  PostEmbedFooterQueryVariables,
} from '../../graphql/generated/graphqlTypes';
import useIsPublicView from '../../utils/useIsPublicView';
import { parsePostMentionId } from '../utils/matchMention';

const POST_EMBED_FOOTER_QUERY = gql`
  query PostEmbedFooter($id: ID!) {
    post(id: $id) {
      id
      title
      ...postPath_post
    }
  }
  ${postPath.fragments.post}
`;

const PUBLIC_POST_EMBED_FOOTER_QUERY = gql`
  query PostEmbedFooterPublic($id: ID!) {
    publicPost(id: $id) {
      id
      title
      ...postPath_post
    }
  }
  ${postPath.fragments.post}
`;

interface PostEmbedFooterProps {
  id: string;
}

const PostEmbedFooter = ({ id }: PostEmbedFooterProps) => {
  const isPublicView = useIsPublicView();
  const { id: postId } = parsePostMentionId(id);

  const { data } = useQuery<
    PostEmbedFooterQuery,
    PostEmbedFooterQueryVariables
  >(POST_EMBED_FOOTER_QUERY, {
    variables: { id: postId },
    skip: isPublicView,
  });

  const { data: publicData } = useQuery<
    PostEmbedFooterPublicQuery,
    PostEmbedFooterPublicQueryVariables
  >(PUBLIC_POST_EMBED_FOOTER_QUERY, {
    variables: { id: postId },
    skip: !isPublicView,
  });

  const post = isPublicView ? publicData?.publicPost : data?.post;

  if (!post) return null;

  return (
    <EmbedFooterLink link={fullOrgUrl(postPath(post))}>
      <EmbedFooterLogoContainer>
        <SVGIcon coloringWith="none" size={16}>
          {slabIcon}
        </SVGIcon>
      </EmbedFooterLogoContainer>
      <EmbedFooterLinkTitle>{post.title}</EmbedFooterLinkTitle>
    </EmbedFooterLink>
  );
};

export default PostEmbedFooter;
